import React, { useCallback, useMemo, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useReactiveVar } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import debounce from 'lodash/debounce'

import { ConditionRender } from 'components'
import { formatLabelOptions } from 'shared/utils/labels'
import { useLoading, useServerError } from 'shared/hooks'
import { currentUserVar, gpsUnitsVar } from 'modulesCommon/store'
import { ArtistCreate, ArtistPreview, ArtistSelection, StepperNavigation } from 'modules/ProjectCreate/components'
import { useSearchArtistByName } from '../serviceHooks/useSearchArtist'

import { IArtist } from 'shared/interfaces'
import { useZonesList } from 'modulesCommon/serviceHooks'

type TArtistStepMode = 'search' | 'create' | 'preview'

const ArtistsStep = () => {
  const navigate = useNavigate()

  const currentUser = useReactiveVar(currentUserVar)
  const gpsUnits = useReactiveVar(gpsUnitsVar)
  const { zoneList } = useZonesList()
  const { getArtistByName, loading: artistLoading, error: artistError, data: artistList } = useSearchArtistByName()

  const { watch, reset, getValues } = useFormContext()

  const artistId = watch('artistId')
  const artistName = watch('artistName')
  const [formMode, setFormMode] = useState<TArtistStepMode>(artistId ? 'preview' : artistName ? 'create' : 'search')

  const labelOptions = useMemo(() => formatLabelOptions(zoneList), [zoneList])
  const searchResults = useMemo(() => artistList?.getArtistsByName || [], [artistList?.getArtistsByName])

  const onSetArtist = useCallback(
    (selectedArtist?: IArtist, shouldResetProduct?: boolean) => {
      reset({
        ...getValues(),
        artistId: selectedArtist?.id ?? '',
        artistName: selectedArtist?.name ?? '',
        firstCreatedIn: selectedArtist?.label_id ?? '',
        gpsStatus: selectedArtist?.gps_status ?? '',
        opusId: selectedArtist?.opus_id ?? '',
        facebookId: selectedArtist?.facebook_id ?? '',
        spotifyId: selectedArtist?.spotify_id ?? '',
        ...(shouldResetProduct && { products: [] })
      })
    },
    [reset, getValues]
  )

  const onSelectArtist = useCallback(
    (selectedArtist: IArtist) => {
      onSetArtist(selectedArtist, true)
      setFormMode('preview')
    },
    [onSetArtist]
  )

  const handleCreate = useCallback(
    (artistName: string) => {
      reset({
        ...getValues(),
        artistName,
        firstCreatedIn: currentUser.label_id,
        products: []
      })
      setFormMode('create')
    },
    [currentUser.label_id, getValues, reset],
  )

  const onClearArtist = useCallback(() => {
    setFormMode('search')
    onSetArtist()
  }, [onSetArtist])

  const searchArtists = useCallback<(searchValue: string) => void>(
    (searchValue) => {
      reset({
        ...getValues(),
        searchedName: searchValue
      })
      return getArtistByName(searchValue)
    },
  [getArtistByName, reset, getValues],
  )

  const debouncedSearchArtist = useRef<(term: string) => void>(debounce(searchArtists, 400))

  useLoading({ isLoading: artistLoading })
  useServerError([artistError])

  const stepperNavProps = {
    onGoBack: artistName ? onClearArtist : () => navigate(-1),
    ...(artistName && { onNext: () => {} })
  }

  return (
    <>
      <StepperNavigation {...stepperNavProps} />
      <ConditionRender
        condition={formMode === 'search'}
        component={
          <ArtistSelection
            onSelectArtist={onSelectArtist}
            options={artistList?.getArtistsByName}
            searchArtists={debouncedSearchArtist.current}
            artistLoading={artistLoading}
            onCreate={handleCreate}
          />
        }
      />
      <ConditionRender
        condition={formMode === 'preview'}
        component={<ArtistPreview gpsUnits={gpsUnits} labelOptions={labelOptions} clearArtist={onClearArtist} />}
      />
      <ConditionRender
        condition={formMode === 'create'}
        component={<ArtistCreate gpsUnits={gpsUnits} labelOptions={labelOptions} artistList={searchResults} />}
      />
    </>
  )
}

export default ArtistsStep
